import type { OfflineCaptureSnapshot, OfflineSyncResponse } from '@/api/offlineCapture'
import type { CaptureScopeV2, CaptureStorageV2, EnqueueCaptureV2, MutationStateV2 } from '@/offline/capture/typesV2'
import type { OfflineWireBatch } from '@/offline/capture/wire'

export interface CaptureSession { scope: CaptureScopeV2; token: string; tokenExpiresAt: number }
export interface CoordinatorState {
  snapshot: OfflineCaptureSnapshot | null; entries: MutationStateV2[]; busy: boolean; recording: boolean
  error: string; serverLocked: boolean; stale: boolean; legacyRecoveryCount: number | null
}
export interface CaptureTransport {
  loadSnapshot(session: CaptureSession, signal: AbortSignal): Promise<OfflineCaptureSnapshot>
  sync(session: CaptureSession, batch: OfflineWireBatch, signal: AbortSignal): Promise<OfflineSyncResponse>
}
const key = (scope: CaptureScopeV2) => JSON.stringify(scope)
function fail(code: string): never { throw Object.assign(new Error(code), { code }) }
export const emptyCoordinatorState = (): CoordinatorState => ({ snapshot: null, entries: [], busy: false, recording: false, error: '', serverLocked: false, stale: false, legacyRecoveryCount: 0 })

/** No JWTs enter persistence. Every continuation and transport dispatch rechecks identity. */
export class OfflineCaptureCoordinator {
  private generation = 0
  private controller = new AbortController()
  private running = false
  private recordTail: Promise<unknown> = Promise.resolve()
  private recordingCount = 0
  private recordVersion = 0
  constructor(readonly state: CoordinatorState, private readonly deps: {
    storage: CaptureStorageV2
    current(): CaptureSession | null
    transport: CaptureTransport
    now?(): number
    lock<T>(name: string, run: () => Promise<T>): Promise<T | undefined>
    onChanged?(): void
    blocked?(scope: CaptureScopeV2, value?: boolean): boolean
  }) {}
  private now() { return this.deps.now?.() ?? Date.now() }
  clear() {
    this.generation++; this.controller.abort(); this.controller = new AbortController()
    Object.assign(this.state, emptyCoordinatorState())
  }
  private capture(network = false) {
    const session = this.deps.current()
    if (!session) fail('OFFLINE_REAUTHENTICATION_REQUIRED')
    if (network && session.tokenExpiresAt <= this.now()) fail('OFFLINE_REAUTHENTICATION_REQUIRED')
    const generation = this.generation
    const valid = () => {
      const current = this.deps.current()
      return generation === this.generation && current?.token === session.token && key(current.scope) === key(session.scope)
    }
    const check = () => { if (!valid()) fail('OFFLINE_ACCOUNT_CHANGED') }
    return { session, check, valid, signal: this.controller.signal }
  }
  async restore() {
    this.clear()
    if (!this.deps.current()) return
    const task = this.capture()
    try {
      const snapshot = await this.deps.storage.getActiveSnapshot(task.session.scope); task.check()
      const entries = await this.deps.storage.list(task.session.scope); task.check()
      this.state.snapshot = snapshot ?? null; this.state.entries = entries
      this.state.serverLocked = this.deps.blocked?.(task.session.scope) ?? false
      // Legacy inventory is informational: a slow older database must not hide
      // validated V2 work or prevent capture from the restored preparation.
      this.state.legacyRecoveryCount = null
      void this.deps.storage.getLegacyRecoveryCount(task.session.scope).then(legacy => {
        if (task.valid()) this.state.legacyRecoveryCount = legacy
      }).catch(() => {
        // Leave inventory unknown. Failure to inspect an older database does
        // not invalidate this preparation or its independently durable work.
      })
    } catch (error) { if (task.valid()) this.state.error = this.code(error) }
  }
  /** Receipt notifications must not clear a usable editor or overwrite a newer tap. */
  async refresh(): Promise<boolean> {
    if (this.running || this.state.recording || !this.deps.current()) return false
    const task = this.capture(), version = this.recordVersion
    try {
      const snapshot = await this.deps.storage.getActiveSnapshot(task.session.scope); task.check()
      const entries = await this.deps.storage.list(task.session.scope); task.check()
      if (this.running || this.state.recording || version !== this.recordVersion) return false
      const pendingIds = this.state.entries.filter(row => row.status === 'pending').map(row => row.mutation.wireMutation.clientMutationId)
      const confirmedIds = new Set(entries.filter(row => row.status === 'confirmed').map(row => row.mutation.wireMutation.clientMutationId))
      this.state.snapshot = snapshot ?? null; this.state.entries = entries
      this.state.serverLocked = this.deps.blocked?.(task.session.scope) ?? false
      // Another coordinator may have replayed our lost response. Clear only that
      // network error, and only after durable receipts confirm every pending ID.
      // Permission, storage and conflict failures still require their own recovery.
      if (this.state.error === 'NETWORK_ERROR' && !this.state.serverLocked && pendingIds.length && pendingIds.every(id => confirmedIds.has(id))) this.state.error = ''
      return true
    } catch (error) { if (task.valid()) this.state.error = this.code(error); return false }
  }
  private code(error: unknown) { return (error as { code?: string })?.code || 'OFFLINE_STORAGE_FAILED' }
  private handle(error: unknown, session: CaptureSession) {
    const value = error as { status?: number; code?: string }
    this.state.error = this.code(error)
    if ([401, 403, 404].includes(value.status || 0)) {
      this.state.serverLocked = true
      this.deps.blocked?.(session.scope, true)
    }
  }
  private async reload(task: ReturnType<OfflineCaptureCoordinator['capture']>) {
    const entries = await this.deps.storage.list(task.session.scope); task.check()
    this.state.entries = entries
  }
  async prepare() {
    const task = this.capture(true)
    if (this.running) return
    this.running = true; this.state.busy = true; this.state.error = ''
    try {
      const snapshot = await this.deps.transport.loadSnapshot(task.session, task.signal); task.check()
      await this.deps.storage.stageSnapshot(task.session.scope, snapshot); task.check()
      await this.deps.storage.activateSnapshot(task.session.scope, snapshot.snapshotId); task.check()
      this.state.snapshot = snapshot; this.state.stale = false; this.state.serverLocked = false
      this.deps.blocked?.(task.session.scope, false)
      await this.reload(task)
      this.deps.onChanged?.()
    } catch (error) { if (task.valid()) this.handle(error, task.session); throw error }
    finally { this.running = false; if (task.valid()) this.state.busy = false }
  }
  async record(input: Omit<EnqueueCaptureV2, 'expectedSnapshotId'>) {
    const task = this.capture()
    const snapshot = this.state.snapshot
    if (this.state.serverLocked) fail('OFFLINE_REAUTHENTICATION_REQUIRED')
    if (!snapshot || this.now() < Date.parse(snapshot.preparedAt) || this.now() >= Date.parse(snapshot.sessionEndsAt)) fail('OFFLINE_PREPARATION_CLOSED')
    // Serialize durable writes in tap order, including rapid taps on different criteria.
    // Capture identity and preparation now: queued work must not follow navigation.
    const value = JSON.parse(JSON.stringify(input)) as typeof input
    this.recordVersion++; this.recordingCount++; this.state.recording = true
    const job = this.recordTail.catch(() => undefined).then(async () => {
      try {
        task.check()
        const mutation = await this.deps.storage.enqueue(task.session.scope, { ...value, expectedSnapshotId: snapshot.snapshotId }); task.check()
        // enqueue has committed. A subsequent inventory failure must not invite a
        // duplicate tap by reporting that this durable command was not saved.
        const known = new Map(this.state.entries.map(entry => [entry.mutation.wireMutation.clientMutationId, entry]))
        known.set(mutation.wireMutation.clientMutationId, { status: 'pending', mutation })
        this.state.entries = [...known.values()].sort((a, b) => a.mutation.wireMutation.sequence - b.mutation.wireMutation.sequence)
        this.state.error = ''
        // Notifications are advisory. Their failure cannot undo this committed
        // mutation or ask the assessor to enter the same observation twice.
        try { this.deps.onChanged?.() } catch { /* Foreground sync still reads the durable outbox. */ }
        return mutation
      } catch (error) { if (task.valid()) this.state.error = this.code(error); throw error }
      finally {
        this.recordingCount--
        if (task.valid()) this.state.recording = this.recordingCount > 0
      }
    })
    this.recordTail = job
    return job
  }
  async sync() {
    const task = this.capture(true)
    if (this.running) return
    this.running = true; this.state.busy = true; this.state.error = ''
    try {
      await this.deps.lock(`coachsight-offline:${task.session.scope.apiOrigin}:${task.session.scope.realActorId}`, async () => {
        task.check()
        await this.reload(task)
        // A bounded foreground pass; another pass handles newly queued work.
        const pending = this.state.entries.filter(entry => entry.status === 'pending')
        let cursor = 0
        while (cursor < pending.length) {
          task.check()
          const first = pending[cursor]!.mutation
          const batch: OfflineWireBatch = { schemaVersion: 1, snapshotId: first.snapshotId, deviceId: first.deviceId, dataEpoch: first.dataEpoch, mutations: [] }
          while (cursor < pending.length && batch.mutations.length < 25) {
            const next = pending[cursor]!.mutation
            if (next.snapshotId !== first.snapshotId || next.deviceId !== first.deviceId || next.dataEpoch !== first.dataEpoch) break
            const candidate = { ...batch, mutations: [...batch.mutations, next.wireMutation] }
            if (new TextEncoder().encode(JSON.stringify(candidate)).length > 128 * 1024) {
              if (!batch.mutations.length) fail('OFFLINE_BATCH_TOO_LARGE')
              break
            }
            batch.mutations.push(next.wireMutation); cursor++
          }
          const reply = await this.deps.transport.sync(task.session, batch, task.signal); task.check()
          const requested = new Set(batch.mutations.map(value => value.clientMutationId))
          if (reply?.schemaVersion !== 1 || !Array.isArray(reply.results) || reply.results.length !== requested.size ||
            new Set(reply.results.map(value => value.clientMutationId)).size !== requested.size || reply.results.some(value => !requested.has(value.clientMutationId))) fail('OFFLINE_INVALID_RECEIPT')
          await this.deps.storage.applyResults(task.session.scope, reply.results); task.check()
          this.state.serverLocked = false; this.deps.blocked?.(task.session.scope, false)
          await this.reload(task); this.deps.onChanged?.()
          // Dependency attention remains immutable; don't hammer a missing predecessor.
          if (reply.results.some(value => value.outcome === 'dependencyPending')) break
        }
      })
    } catch (error) { if (task.valid()) this.handle(error, task.session); throw error }
    finally { this.running = false; if (task.valid()) this.state.busy = false }
  }
}
